import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  api, getToken, setToken, clearToken,
  isTokenExpired, decodeJwtExp, onSessionInvalid,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionInvalidReason, setSessionInvalidReason] = useState(null);
  const expiryTimer = useRef(null);

  const _scheduleExpiryLogout = useCallback((token) => {
    if (expiryTimer.current) { clearTimeout(expiryTimer.current); expiryTimer.current = null; }
    const exp = decodeJwtExp(token);
    if (!exp) return;
    const msLeft = exp * 1000 - Date.now();
    if (msLeft <= 0) return;
    // Cap timer at 24h to avoid setTimeout overflow surprises.
    const clamped = Math.min(msLeft, 24 * 60 * 60 * 1000);
    expiryTimer.current = setTimeout(() => {
      clearToken();
      setUser(null);
      setSessionInvalidReason("expired");
    }, clamped);
  }, []);

  const refresh = useCallback(async () => {
    const t = getToken();
    if (!t || isTokenExpired(t)) {
      clearToken();
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      _scheduleExpiryLogout(t);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [_scheduleExpiryLogout]);

  useEffect(() => { refresh(); }, [refresh]);

  // Central listener for 401 / server-side revocation invalidations.
  useEffect(() => {
    return onSessionInvalid(() => {
      setUser(null);
      setSessionInvalidReason("revoked");
    });
  }, []);

  const loginWithToken = async (accessToken, userData) => {
    setToken(accessToken);
    setSessionInvalidReason(null);
    if (userData) setUser(userData);
    else await refresh();
    _scheduleExpiryLogout(accessToken);
  };

  // Real logout: revoke server-side, then clear local state.
  const signOut = useCallback(async ({ reason } = {}) => {
    const t = getToken();
    if (t) {
      try {
        await api.post("/auth/logout");
      } catch {
        // Even if the server call fails (offline / 401), we still clear locally.
      }
    }
    if (expiryTimer.current) { clearTimeout(expiryTimer.current); expiryTimer.current = null; }
    clearToken();
    setUser(null);
    if (reason) setSessionInvalidReason(reason);
  }, []);

  const clearSessionBanner = useCallback(() => setSessionInvalidReason(null), []);

  return (
    <AuthContext.Provider value={{
      user, loading, loginWithToken, signOut, refresh,
      sessionInvalidReason, clearSessionBanner,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
